/**
 * Sessions Filter & Pagination
 * Handles filtering and pagination for theatre event session cards
 */
function initSessionsFilters() {
    const panel = document.querySelector('[data-sessions-panel]');
    if (!panel) return;

    // Config
    const DESKTOP_LIMIT = parseInt(panel.dataset.desktopLimit) || 12;
    const MOBILE_LIMIT = parseInt(panel.dataset.mobileLimit) || 4;
    const MOBILE_BREAKPOINT = 768;

    // Elements
    const sessionCards = Array.from(panel.querySelectorAll('[data-session-card]'));
    const filterSelects = panel.querySelectorAll('[data-filter-select]');
    const accessibilityDropdown = panel.querySelector('[data-filter-dropdown="accessibility"]');
    const accessibilityCheckboxes = panel.querySelectorAll('[data-accessibility-filter]');
    const multiToggle = panel.querySelector('[data-multi-toggle]');
    const multiPanel = panel.querySelector('[data-multi-panel]');
    const multiClear = panel.querySelector('[data-multi-clear]');
    const clearFiltersBtn = panel.querySelector('[data-clear-filters]');
    const showMoreBtn = panel.querySelector('[data-show-more]');
    const paginationControls = panel.querySelector('[data-pagination-controls]');
    const noResultsMsg = panel.querySelector('[data-no-results]');
    const sessionsGrid = panel.querySelector('[data-sessions-grid]');

    // State - past defaults to 'upcoming' if past events filter exists
    const pastFilterSelect = panel.querySelector('[data-filter-select="past"]');
    let activeFilters = {
        month: '',
        age: '',
        type: '',
        time: '',
        past: pastFilterSelect ? 'upcoming' : '',
        status: '',
        venue: ''
    };
    let currentPage = 1;

    // Handle desktop default open for filter accordion
    const filterAccordion = panel.querySelector('[data-tw-toggle-container][data-desktop-default-open="true"]');
    const setDesktopDefaultOpen = () => {
        if (filterAccordion && window.innerWidth >= MOBILE_BREAKPOINT) {
            filterAccordion.dataset.open = 'true';
        }
    };
    setDesktopDefaultOpen();

    // Utility functions
    const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;
    const getLimit = () => isMobile() ? MOBILE_LIMIT : DESKTOP_LIMIT;
    const hasActiveFilters = () => {
        // Note: 'past' filter with 'upcoming' is the default, so only count as "active" if changed to 'all' or 'past'
        const otherFiltersActive = ['month', 'age', 'type', 'time', 'status', 'venue'].some(k => activeFilters[k] !== '');
        const pastChanged = activeFilters.past !== 'upcoming' && activeFilters.past !== '';
        // Accessibility is "active" if some (but not all) checkboxes are checked
        const checkedCount = Array.from(accessibilityCheckboxes).filter(cb => cb.checked).length;
        const accessibilityActive = checkedCount > 0 && checkedCount < accessibilityCheckboxes.length;
        return otherFiltersActive || pastChanged || accessibilityActive;
    };

    // Close multi-select dropdown
    const closeMultiPanel = () => {
        if (multiPanel) multiPanel.classList.add('hidden');
    };

    // Apply filters and pagination
    const applyFiltersAndPagination = () => {
        const checkedAccessibility = Array.from(accessibilityCheckboxes)
            .filter(cb => cb.checked)
            .map(cb => cb.dataset.accessibilityFilter);
        const allAccessibilityChecked = checkedAccessibility.length === accessibilityCheckboxes.length;

        let visibleCount = 0;
        let matchingCards = [];

        // First pass: determine which cards match filters
        sessionCards.forEach(card => {
            let matches = true;

            // Past events filter: 'upcoming' (default) hides past, 'past' shows only past, 'all' shows everything
            if (activeFilters.past === 'upcoming' && card.dataset.past === 'true') matches = false;
            if (activeFilters.past === 'past' && card.dataset.past !== 'true') matches = false;

            // Status/Availability filter
            if (activeFilters.status && card.dataset.status !== activeFilters.status) matches = false;

            // Venue filter
            if (activeFilters.venue && card.dataset.venue !== activeFilters.venue) matches = false;

            if (activeFilters.month && card.dataset.month !== activeFilters.month) matches = false;
            if (activeFilters.age && card.dataset.age !== activeFilters.age) matches = false;
            if (activeFilters.type) {
                const cardTypes = card.dataset.type ? card.dataset.type.split(',') : [];
                if (!cardTypes.includes(activeFilters.type)) matches = false;
            }
            if (activeFilters.time && card.dataset.time !== activeFilters.time) matches = false;

            // Accessibility filter: if some checkboxes are checked, only show cards with those features
            // If ALL are checked (default), show everything. If NONE are checked, show everything.
            const someAccessibilityChecked = checkedAccessibility.length > 0 && !allAccessibilityChecked;
            if (someAccessibilityChecked) {
                const cardAccessibility = card.dataset.accessibility ? card.dataset.accessibility.split(',').filter(v => v) : [];
                const hasMatchingAccessibility = checkedAccessibility.some(a => cardAccessibility.includes(a));
                if (!hasMatchingAccessibility) matches = false;
            }

            card.dataset.filtered = matches ? 'false' : 'true';
            if (matches) matchingCards.push(card);
        });

        // Pagination
        const filtersActive = hasActiveFilters();
        const limit = getLimit();
        const maxVisible = filtersActive ? matchingCards.length : (currentPage * limit);

        matchingCards.forEach((card, index) => {
            if (index < maxVisible) {
                card.style.display = 'block';
                card.dataset.paginated = 'false';
                visibleCount++;
            } else {
                card.style.display = 'none';
                card.dataset.paginated = 'true';
            }
        });

        sessionCards.forEach(card => {
            if (card.dataset.filtered === 'true') card.style.display = 'none';
        });

        // Update UI elements
        if (showMoreBtn && paginationControls) {
            paginationControls.classList.toggle('hidden', filtersActive || visibleCount >= matchingCards.length);
        }
        if (clearFiltersBtn) {
            clearFiltersBtn.classList.toggle('hidden', !filtersActive);
        }
        if (noResultsMsg && sessionsGrid) {
            noResultsMsg.classList.toggle('hidden', matchingCards.length > 0);
            sessionsGrid.classList.toggle('hidden', matchingCards.length === 0);
        }
    };

    // Native select change handlers
    filterSelects.forEach(select => {
        select.addEventListener('change', () => {
            const filterType = select.dataset.filterSelect;
            activeFilters[filterType] = select.value;
            currentPage = 1;
            applyFiltersAndPagination();
        });
    });

    // Multi-select toggle (accessibility dropdown)
    if (multiToggle && multiPanel) {
        multiToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            multiPanel.classList.toggle('hidden');
        });
    }

    // Accessibility checkbox changes
    accessibilityCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
            currentPage = 1;
            applyFiltersAndPagination();
        });
    });

    // Multi-select clear button
    if (multiClear) {
        multiClear.addEventListener('click', () => {
            accessibilityCheckboxes.forEach(cb => cb.checked = false);
            currentPage = 1;
            applyFiltersAndPagination();
        });
    }

    // Clear all filters
    if (clearFiltersBtn) {
        clearFiltersBtn.addEventListener('click', () => {
            activeFilters = {
                month: '',
                age: '',
                type: '',
                time: '',
                past: pastFilterSelect ? 'upcoming' : '',
                status: '',
                venue: ''
            };
            filterSelects.forEach(select => {
                // Reset past filter to 'upcoming', others to empty
                if (select.dataset.filterSelect === 'past') {
                    select.value = 'upcoming';
                } else {
                    select.value = '';
                }
            });
            accessibilityCheckboxes.forEach(cb => cb.checked = true);
            currentPage = 1;
            applyFiltersAndPagination();
        });
    }

    // Show More button
    if (showMoreBtn) {
        showMoreBtn.addEventListener('click', () => {
            currentPage++;
            applyFiltersAndPagination();
        });
    }

    // Close multi-panel when clicking outside
    document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-filter-dropdown="accessibility"]')) {
            closeMultiPanel();
        }
    });

    // Handle resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(applyFiltersAndPagination, 150);
    });

    // Preset type filter from URL parameter (e.g. ?sessionType=schoolSeasonPerformance)
    const urlParams = new URLSearchParams(window.location.search);
    const presetType = urlParams.get('sessionType');
    if (presetType) {
        const typeSelect = panel.querySelector('[data-filter-select="type"]');
        if (typeSelect) {
            typeSelect.value = presetType;
            activeFilters.type = presetType;
        }
    }

    // Initial application
    applyFiltersAndPagination();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSessionsFilters);
} else {
    initSessionsFilters();
}

